"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BulkDistributeCouponResponseSchema = exports.BulkDistributeCouponBodySchema = exports.OverseasSimulatorBodySchema = exports.OverseasSimulatorQuerySchema = exports.SimulatorQuerySchema = exports.SimulatorResponseSchema = exports.StayType = void 0;
var zod_1 = require("zod");
/**
 * 숙박 유형 값. IDE에서 각 키에 hover 시 한글 라벨이 표시된다.
 * 호스트는 문자열 리터럴(`'SLEEP'`) 대신 `StayType.SLEEP`로 참조하면 자동완성·hover 도움을 받을 수 있다.
 */
exports.StayType = {
    /** 숙박 */
    SLEEP: 'SLEEP',
    /** 대실 */
    REST: 'REST',
    /** 연박 */
    DAYS: 'DAYS',
};
var PaymentBenefitContentSchema = zod_1.z.object({
    title: zod_1.z.string(),
    body: zod_1.z.string(),
});
var PaymentBenefitSchema = zod_1.z.object({
    // 결제 수단 코드 — upstream PaymentMethodCode 보다 넓을 수 있음 (Toss/Npay 등 확장). enum 대신 string.
    code: zod_1.z.string(),
    // 결제 수단 표시명. e.g., "NOL 머니"
    name: zod_1.z.string(),
    // 로고 URL. upstream에서 nullish.
    iconUrl: zod_1.z.string().nullish(),
    // promotionBadgeText. e.g., "첫결제시" / "선착순". 없으면 null.
    tagText: zod_1.z.string().nullish(),
    // 해당 결제 수단의 혜택 콘텐츠. 보통 1개지만 다중도 가능.
    contents: zod_1.z.array(PaymentBenefitContentSchema),
});
var SaleInfoDetailSchema = zod_1.z.object({
    kind: zod_1.z.enum(['BASIC', 'MEMBERSHIP', 'PROMOTION', 'COUPON', 'HOT_DEAL']).nullish(),
    title: zod_1.z.string(),
    description: zod_1.z.string().nullish(),
    appliedPrice: zod_1.z.string(),
});
var SaleInfoSchema = zod_1.z.object({
    title: zod_1.z.string(),
    titleDescription: zod_1.z.string().nullish(),
    titleSuffix: zod_1.z.string().nullish(),
    totalStartingRate: zod_1.z.string(),
    totalStartingRateDescription: zod_1.z.string().nullish(),
    details: zod_1.z.array(SaleInfoDetailSchema),
    totalRate: zod_1.z.string(),
    totalRateDescription: zod_1.z.string().nullish(),
});
var PriceBreakdownPriceNolPointSchema = zod_1.z.object({
    // NOL 포인트 보유 잔액 표시 텍스트. 예: "(38,000P 보유)".
    balance: zod_1.z.string(),
    totalAfterApplied: zod_1.z.string(),
});
// 해외 전용 — 국내는 항상 empty array.
var PriceBreakdownPriceBadgeSchema = zod_1.z.object({
    type: zod_1.z.string(),
    theme: zod_1.z.string(),
    label: zod_1.z.string().nullish(),
});
var PriceBreakdownPriceSchema = zod_1.z.object({
    promotionLabel: zod_1.z.string().nullish(),
    promotionRatio: zod_1.z.number().nullish(),
    totalRate: zod_1.z.string(),
    /** 전체 할인 합계 표시 텍스트. 예: "41,000원 할인 적용됨". 할인 없으면 null. */
    discountApplied: zod_1.z.string().nullish(),
    /** 박당 평균 가격 표시 텍스트 (할인 적용 후, 1박당 환산). 예: "(1박 평균 120,000원)". 단일 박이거나 미산출 시 null. */
    totalRateDescription: zod_1.z.string().nullish(),
    nolPoint: PriceBreakdownPriceNolPointSchema.nullish(),
    /** 멤버십/상품 뱃지 목록. 해외 전용 — 국내는 항상 empty array. */
    badges: zod_1.z.array(PriceBreakdownPriceBadgeSchema).default([]),
});
// stay-api(PriceBreakdownV2Coupon) 응답을 BFF에서 변환 없이 그대로 전달한다.
// 필드명/타입은 upstream과 동일 — 호스트는 stay-api 어휘를 그대로 사용.
var PriceSimulatorCouponSchema = zod_1.z.object({
    /**
     * 쿠폰 식별자. 서버가 BIGINT 정밀도 보존을 위해 string으로 직렬화 (예: "1000000000001255").
     * 해외는 상류(global-stay-api) 패스스루라 누락 가능 — nullish.
     */
    couponId: zod_1.z.string().nullish(),
    /** 쿠폰 노출명. 누락 가능. */
    title: zod_1.z.string().nullish(),
    /** 쿠폰 카테고리 — GENERAL/PLCC/REWARD/MILLION/DM 등. 자동 적용 쿠폰은 null일 수 있음. */
    couponType: zod_1.z.string().nullish(),
    /** 쿠폰 종류 분기용 (PLCC 안내 배너 노출 신호 등). 누락 가능. */
    couponKind: zod_1.z.string().nullish(),
    /** 할인 방식 — AMOUNT(금액) / RATE(비율). */
    discountType: zod_1.z.string().nullish(),
    discountAmount: zod_1.z.number().nullish(),
    discountRate: zod_1.z.number().nullish(),
    /** plccCoupons에서는 가격 계산 미반영 — 항상 null. */
    discountFinalAmount: zod_1.z.number().nullish(),
    /**
     * 다운로드 식별자. **string으로 직렬화됨** (BulkDistribute API에 그대로 전달 가능).
     * null/undefined면 자동 적용 쿠폰 — UI는 "받기" 액션 숨김.
     */
    distributionId: zod_1.z.string().nullish(),
    /** 배포 경로 — PROMOTION일 때만 사용자가 받기 가능. 그 외 자동/기보유. */
    distributionType: zod_1.z.string().nullish(),
    /** 보유 여부. **upstream 이름은 `downloaded`**. true면 "보유중" UI. 누락 시 미보유로 간주. */
    downloaded: zod_1.z.boolean().nullish(),
    /** 중복(더하기) 쿠폰 허용 여부. 해외 전용. */
    allowAdditional: zod_1.z.boolean().nullish(),
    /** 남은 재고. 해외 전용. */
    remain: zod_1.z.number().nullish(),
    /**
     * 사용 가능 결제수단 목록. 일반 coupons에서는 항상 null — plccCoupons 전용 필드.
     * 해외 전용.
     */
    allowedPaymentMethods: zod_1.z
        .array(zod_1.z.object({
        code: zod_1.z.string().nullish(),
        codeDescription: zod_1.z.string().nullish(),
        subCode: zod_1.z.string().nullish(),
        subCodeDescription: zod_1.z.string().nullish(),
    }))
        .nullish(),
});
exports.SimulatorResponseSchema = zod_1.z.object({
    isLoggedIn: zod_1.z.boolean(),
    // 결제수단 혜택 — pay-policy의 paymentPolicies와 promotions.paymentMethodPromotions를 BFF에서 join + groupBy로 가공한 결과
    paymentBenefits: zod_1.z.array(PaymentBenefitSchema),
    // priceBreakdown (stay-api / global-stay-api) spread 결과
    saleInfo: SaleInfoSchema.nullish(),
    price: PriceBreakdownPriceSchema,
    // priceSimulator (price-api) ratePlans[0].benefit.coupons 추출
    coupons: zod_1.z.array(PriceSimulatorCouponSchema),
    /** 결제수단 제한(PLCC 등) 쿠폰 목록. 해외 전용 — 국내는 항상 empty array. */
    plccCoupons: zod_1.z.array(PriceSimulatorCouponSchema).default([]),
});
var DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
var POSITIVE_INTEGER_PATTERN = /^[1-9]\d*$/;
// eslint-disable-next-line prefer-named-capture-group
var ADULT_PAX_PATTERN = /^([1-9]|[12]\d|30)$/;
// eslint-disable-next-line prefer-named-capture-group
var NON_NEGATIVE_INTEGER_LIST_PATTERN = /^\d+(,\d+)*$/;
exports.SimulatorQuerySchema = zod_1.z.object({
    roomTypeId: zod_1.z
        .string({
        required_error: 'roomTypeId는 필수 입력값입니다.',
        invalid_type_error: 'roomTypeId는 문자열이어야 합니다.',
    })
        .regex(POSITIVE_INTEGER_PATTERN, 'roomTypeId는 양의 정수 문자열이어야 합니다.'),
    ratePlanId: zod_1.z
        .string({
        required_error: 'ratePlanId는 필수 입력값입니다.',
        invalid_type_error: 'ratePlanId는 문자열이어야 합니다.',
    })
        .regex(POSITIVE_INTEGER_PATTERN, 'ratePlanId는 양의 정수 문자열이어야 합니다.'),
    checkInDate: zod_1.z
        .string({
        required_error: 'checkInDate는 필수 입력값입니다.',
        invalid_type_error: 'checkInDate는 문자열이어야 합니다.',
    })
        .regex(DATE_PATTERN, 'checkInDate는 yyyy-MM-dd 형식이어야 합니다.'),
    checkOutDate: zod_1.z
        .string({
        required_error: 'checkOutDate는 필수 입력값입니다.',
        invalid_type_error: 'checkOutDate는 문자열이어야 합니다.',
    })
        .regex(DATE_PATTERN, 'checkOutDate는 yyyy-MM-dd 형식이어야 합니다.'),
    adultPax: zod_1.z
        .string({
        required_error: 'adultPax는 필수 입력값입니다.',
        invalid_type_error: 'adultPax는 문자열이어야 합니다.',
    })
        .regex(ADULT_PAX_PATTERN, 'adultPax는 1~30 사이 정수 문자열이어야 합니다.'),
    childrenAges: zod_1.z
        .string({ invalid_type_error: 'childrenAges는 문자열이어야 합니다.' })
        .regex(NON_NEGATIVE_INTEGER_LIST_PATTERN, 'childrenAges는 콤마 구분 0 이상 정수 문자열이어야 합니다.')
        .optional(),
    baseDate: zod_1.z
        .string({ invalid_type_error: 'baseDate는 문자열이어야 합니다.' })
        .regex(DATE_PATTERN, 'baseDate는 yyyy-MM-dd 형식이어야 합니다.')
        .optional(),
    baseTime: zod_1.z
        .string({ invalid_type_error: 'baseTime은 문자열이어야 합니다.' })
        .regex(/^\d{2}:\d{2}$/, 'baseTime은 HH:mm 형식이어야 합니다.')
        .optional(),
    propertyId: zod_1.z
        .string({
        required_error: 'propertyId는 필수 입력값입니다.',
        invalid_type_error: 'propertyId는 문자열이어야 합니다.',
    })
        .regex(POSITIVE_INTEGER_PATTERN, 'propertyId는 양의 정수 문자열이어야 합니다.'),
    propertyCategory: zod_1.z.enum(['MOTEL', 'HOTEL', 'GUEST_HOUSE', 'PENSION'], {
        required_error: 'propertyCategory는 필수 입력값입니다.',
        invalid_type_error: 'propertyCategory는 MOTEL/HOTEL/GUEST_HOUSE/PENSION 중 하나여야 합니다.',
    }),
});
// 해외 — 다중 객실(rooms[]) 지원. 국내 SimulatorFilter(단일 adultPax/childrenAges)와 구조적으로 분리.
var RoomOccupancySchema = zod_1.z.object({
    adultPax: zod_1.z
        .number({
        required_error: 'adultPax는 필수 입력값입니다.',
        invalid_type_error: 'adultPax는 숫자여야 합니다.',
    })
        .int()
        .positive()
        .max(8, 'adultPax는 8 이하여야 합니다.'),
    childrenAges: zod_1.z.array(zod_1.z.number().int().min(0).max(17)).max(8).optional(),
});
// 원칙: 스칼라 값은 query string, 구조화된(배열) 값이거나 encoding에 취약한 불투명 토큰은 body.
// 국내(전부 스칼라·URL-safe → 전부 query)와 같은 규칙이며, 데이터 성격이 달라 결과 shape만 다르다.
exports.OverseasSimulatorQuerySchema = zod_1.z.object({
    // 국내(propertyId)와 달리 순수 정수가 아닌 "TDP-123507" 형태의 식별자 — 정규식 대신 non-empty만 검증.
    propertyId: zod_1.z
        .string({
        required_error: 'propertyId는 필수 입력값입니다.',
        invalid_type_error: 'propertyId는 문자열이어야 합니다.',
    })
        .min(1, 'propertyId는 빈 문자열일 수 없습니다.'),
    roomTypeId: zod_1.z
        .string({
        required_error: 'roomTypeId는 필수 입력값입니다.',
        invalid_type_error: 'roomTypeId는 문자열이어야 합니다.',
    })
        .min(1, 'roomTypeId는 빈 문자열일 수 없습니다.'),
    checkInDate: zod_1.z
        .string({
        required_error: 'checkInDate는 필수 입력값입니다.',
        invalid_type_error: 'checkInDate는 문자열이어야 합니다.',
    })
        .regex(DATE_PATTERN, 'checkInDate는 yyyy-MM-dd 형식이어야 합니다.'),
    checkOutDate: zod_1.z
        .string({
        required_error: 'checkOutDate는 필수 입력값입니다.',
        invalid_type_error: 'checkOutDate는 문자열이어야 합니다.',
    })
        .regex(DATE_PATTERN, 'checkOutDate는 yyyy-MM-dd 형식이어야 합니다.'),
});
exports.OverseasSimulatorBodySchema = zod_1.z.object({
    // ratePlanId 대응 개념. 국내와 이름을 통일하지 않는다 (upstream 응답 필드명과 일치시켜 추적성 확보).
    // base64 계열 불투명 토큰이라 +, /, = 등 URL 인코딩에 취약 — query string이 아닌 body로 전달.
    provisionCode: zod_1.z
        .string({
        required_error: 'provisionCode는 필수 입력값입니다.',
        invalid_type_error: 'provisionCode는 문자열이어야 합니다.',
    })
        .min(1, 'provisionCode는 빈 문자열일 수 없습니다.'),
    // upstream 문서상 null 불가하나 empty array는 허용.
    rooms: zod_1.z.array(RoomOccupancySchema, {
        required_error: 'rooms는 필수 입력값입니다.',
        invalid_type_error: 'rooms는 배열이어야 합니다.',
    }),
});
exports.BulkDistributeCouponBodySchema = zod_1.z.object({
    // PriceSimulatorCoupon.distributionId가 string이므로 일관성 위해 동일하게 string.
    // BFF는 promotion-api 호출 직전에 Number()로 변환.
    distributionIds: zod_1.z
        .array(zod_1.z
        .string({ invalid_type_error: 'distributionId는 문자열이어야 합니다.' })
        .regex(/^[1-9]\d*$/, 'distributionId는 양의 정수 문자열이어야 합니다.'), {
        required_error: 'distributionIds는 필수 입력값입니다.',
        invalid_type_error: 'distributionIds는 배열이어야 합니다.',
    })
        .min(1, 'distributionIds는 최소 1개 이상이어야 합니다.')
        .max(5, 'distributionIds는 최대 5개까지 가능합니다.'),
});
var BulkDistributeCouponResultSchema = zod_1.z.object({
    distributionId: zod_1.z.string(),
    succeed: zod_1.z.boolean(),
    reason: zod_1.z.string().nullish(),
});
exports.BulkDistributeCouponResponseSchema = zod_1.z.object({
    results: zod_1.z.array(BulkDistributeCouponResultSchema),
});
